/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import {
  addPermissionToRole,
  addRoleForOffice,
  getDescriptionRole,
  getPermissionsForRole,
  getRolesForOffice,
  getUsersInRole,
  removePermissionFromRole,
  removeRoleInOffice,
} from "@/requests/administration/administrationRoles";
import { RoleDescription } from "@/models/components/admin/RoleDescription";
import { OfficeRole } from "@/models/api/administration/roles/OfficeRole";
import { ADMINISTRATION_ROLES_TABS } from "@/components/administration/roles/AdministrationRolesContentMenu.vue";
import { UserInRole } from "@/models/api/administration/roles/UserInRole";
import { addUserRole, removeUserRole } from "@/requests/administration/administrationUsers";
import { PermissionGroup, PermissionGroupItem } from "@/models/api/administration/roles/PermissionGroup";
import { NewRole } from "@/models/api/administration/roles/NewRole";

export interface AdministrationRolesStateInterface {
  roleDescriptions: Array<RoleDescription>;
  selectedOfficeId: null | string;
  selectedRoleId: null | string;
  roles: Array<OfficeRole>;
  selectedTab: ADMINISTRATION_ROLES_TABS;
  usersInRole: Array<UserInRole>;
  permissions: Array<PermissionGroup>;
  isAddRoleDialog: boolean;
}

export const AdministrationRolesState: AdministrationRolesStateInterface = {
  roleDescriptions: [],
  selectedOfficeId: null,
  selectedRoleId: null,
  roles: [],
  selectedTab: ADMINISTRATION_ROLES_TABS.Permissions,
  usersInRole: [],
  permissions: [],
  isAddRoleDialog: false,
};

const AdministrationRolesModule = defineModule({
  state: AdministrationRolesState as AdministrationRolesStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    clearRoleDescriptions(state) {
      state.roleDescriptions = [];
    },
    cacheRoleDescription(state, param: RoleDescription) {
      state.roleDescriptions.push(param);
    },
    setSelectedOfficeId(state, param: string | null) {
      state.selectedOfficeId = param;
      state.selectedRoleId = null;
    },
    setSelectedRoleId(state, param: string | null) {
      state.selectedRoleId = param;
    },
    setRoles(state, param: Array<OfficeRole>) {
      state.roles = param;
      state.usersInRole = [];
    },
    setSelectedTab(state, param: ADMINISTRATION_ROLES_TABS) {
      state.selectedTab = param;
    },
    setUsersInRole(state, param: Array<UserInRole>) {
      state.usersInRole = param;
    },
    setPermissions(state, param: Array<PermissionGroup>) {
      state.permissions = param;
    },
    setIsAddRoleDialog(state, param: boolean) {
      state.isAddRoleDialog = param;
    },
  },
  actions: {
    async getRoleDescription(
      context,
      params: { roleId: string; officeId: string; name: string }
    ): Promise<RoleDescription> {
      const { commit, state } = _moduleActionContext(context);
      if (
        typeof state.roleDescriptions.find((v) => v.id === params.roleId && v.officeId === params.officeId) ===
        "undefined"
      ) {
        const descriptionRole = await getDescriptionRole(params.roleId, params.officeId);
        commit.cacheRoleDescription(new RoleDescription(descriptionRole, params.roleId, params.name, params.officeId));
      }
      return state.roleDescriptions.find(
        (v) => v.id === params.roleId && v.officeId === params.officeId
      ) as RoleDescription;
    },
    async downloadRoleList(context, skipSetDefault: boolean | undefined) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedOfficeId !== null) {
        const roles = await getRolesForOffice(state.selectedOfficeId);
        commit.setRoles(roles);
        if (!skipSetDefault && roles.length > 0) {
          commit.setSelectedRoleId(roles.sort()[0].id);
        }
      }
    },
    async downloadUsersInRole(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedOfficeId !== null && state.selectedRoleId) {
        const usersInRole = await getUsersInRole(state.selectedRoleId, state.selectedOfficeId);
        commit.setUsersInRole(usersInRole);
      }
    },
    async toggleUserInRole(context, userId: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.waitForCompleteAllRequests();
      if (state.selectedRoleId !== null && state.selectedOfficeId !== null) {
        const role = state.usersInRole.find((v) => v.id === userId) as UserInRole;
        if (role.isInRole) {
          await removeUserRole(role.id, state.selectedOfficeId, state.selectedRoleId);
        } else {
          await addUserRole(role.id, state.selectedOfficeId, state.selectedRoleId);
        }
        rootDispatch.popup.addOperationSuccessfulSnackbar();
        await dispatch.downloadUsersInRole();
      }
    },
    async downloadRolePermissions(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.selectedRoleId !== null && state.selectedOfficeId !== null) {
        const permissions = await getPermissionsForRole(state.selectedRoleId, state.selectedOfficeId);
        commit.setPermissions(permissions);
      }
    },
    async addRoleForOffice(context, newRole: NewRole) {
      const { commit, state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedOfficeId !== null) {
        const roleId = await addRoleForOffice(state.selectedOfficeId, newRole);
        commit.setIsAddRoleDialog(false);
        rootDispatch.popup.addSavedOkSnackbar();
        dispatch.downloadRoleList(true);
        commit.setSelectedRoleId(roleId);
      }
    },
    async removeRoleFromOffice(context, roleId: string) {
      const { state, dispatch, rootDispatch } = _moduleActionContext(context);
      if (state.selectedOfficeId !== null) {
        if (await rootDispatch.popup.addConfirmRemoveElement()) {
          await removeRoleInOffice(roleId, state.selectedOfficeId);
          rootDispatch.popup.addOperationSuccessfulSnackbar();
          dispatch.downloadRoleList();
        }
      }
    },
    async toggleRolePermission(context, permissionGroupItem: PermissionGroupItem) {
      const { state, rootDispatch } = _moduleActionContext(context);
      if (state.selectedRoleId !== null && state.selectedOfficeId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        if (!permissionGroupItem.isActive) {
          await removePermissionFromRole(state.selectedRoleId, state.selectedOfficeId, permissionGroupItem.id);
        } else {
          await addPermissionToRole(state.selectedRoleId, state.selectedOfficeId, permissionGroupItem.id);
        }
        rootDispatch.popup.addOperationSuccessfulSnackbar();
      }
    },
  },
});

export default AdministrationRolesModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) => moduleGetterContext(args, AdministrationRolesModule);
const _moduleActionContext = (context: any) => moduleActionContext(context, AdministrationRolesModule);
